import { Component, OnInit } from '@angular/core';
import { LoaderService } from 'src/app/loader.service';
import { Button, Pagination, Table, TableData, TableDataTypes } from 'src/app/shared/types';
import { BulkPayArrayFields, BulkPayService } from '../bulkpay.service';
import * as moment from 'moment';
import { AlertDialogOptions, AlertDialogService } from 'src/app/shared/alert-dialog/alert-dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BULK_PAY_HISTORY } from 'src/app/routes';
import Utils from 'src/app/utils/utils';

@Component({
  selector: 'app-bulkpay-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.css']
})
export class BulkPayHistoryComponent implements OnInit {
  public table: Table = new Table({
    pagination: new Pagination(0, 10, 100, [5, 10, 15, 20]),
    headers: {
      createdAt: 'Date',
      createdBy: 'Initiated By',
      totalTransactions: 'Number of captains',
      totalAmount: 'Total Amount',
      subTransactionType: 'Sub Transaction',
      status: 'Status',
      result: 'Download result',
    },
    data: []
  });
  private sortableColumns: string[] = ["createdAt"];
  private historyQuery = {
    limit: 10,
    offset: 0,
    sortBy: "createdAt",
    sortOrder: -1,
    subTransactionType: [] as string[],
    from: undefined as number | undefined,
    to: undefined as number | undefined,
  };

  showFilters = false;
  filterComponents = [
    {
      type: 'searchable-dropdown',
      title: 'Subtransaction Type',
      values: this.bulkPayService.transactionTypes.map(t => t.displayName),
      key: 'subTransactionType'
    },
    {
      type: 'calendar',
      title: 'Created Date',
      key: 'createdAt'
    }
  ];

  constructor(private bulkPayService: BulkPayService,
    private loader: LoaderService,
    private alertService: AlertDialogService,
    private currentRoute: ActivatedRoute,
    private router: Router) { }

  showAlertBox(options: AlertDialogOptions, cb = () => { }) {
    this.alertService.open(options);
    this.alertService.confirmed().subscribe(confirmed => confirmed && cb());
  }

  ngOnInit() {
    this.currentRoute.queryParams
      .subscribe(async params => {
        if (params.downloadResult) {
          await this.downloadResultCSV(params.downloadResult);
          this.router.navigateByUrl(BULK_PAY_HISTORY);
        } else {
          await this.fetchHistory();
        };
      });
  }

  async fetchHistory() {
    this.loader.openLoading();
    try {
      this.addPageDetails();
      const query = this.buildHistoryQuery();
      const history = await this.bulkPayService.fetchBulkPayHistory(query) as any[];
      this.logUnknownSubTransactionTypes(history);
      this.table.data = history.map(item => ({
        ...item,
        subTransactionType: this.subTransactionTypeName(item.subTransactionType),
        createdAt: new Date(item.createdAt).toLocaleDateString(),
        result: new TableData({
          type: TableDataTypes.BUTTON,
          button: new Button({
            label: "Download Result",
            className: "btn btn-secondary",
            onClick: () => this.downloadResultCSV(this.fileId(item)),
          })
        })
      }));
    } catch (e) {
      this.table.data = [];
      console.error("[BulkPayHistory] Failed to fetch history", e);
      this.showAlertBox({
        title: "Error",
        message: "Something went wrong while fetching history",
      });
    } finally {
      this.loader.closeLoading();
    }
  }

  async downloadResultCSV(fileId: string) {
    this.loader.openLoading();
    try {
      const { file, transactions } = await this.bulkPayService.downloadResult(fileId) as any;

      const subTransactionType = this.bulkPayService.transactionTypes
        .find(t => t.name === file.subTransactionType);
      if (!subTransactionType) {
        console.error("[BulkPayHistory] Cannot download result, subTransactionType not in config", {
          subTransactionType: file.subTransactionType,
          fileId,
          configuredTypes: this.bulkPayService.transactionTypes.map(t => t.name),
        });
        throw new Error(`subTransactionType not found in config: ${file.subTransactionType}`);
      }

      const formatArrayFields: (value: any) => any = transaction => {
        return BulkPayArrayFields.reduce((item, field) => {
          const value = transaction[field];
          if (!value) {
            return item;
          }
          item[field] = value.join("|");
          return item;
        }, transaction);
      };

      Utils.downloadCSVFile({
        data: transactions.map(formatArrayFields),
        fields: subTransactionType.headers.concat("status"),
        fileName: `${file.subTransactionType}_${file.createdAt}_result.csv`.toLowerCase()
      });
    } catch (e) {
      console.error("[BulkPayHistory] Failed to download result", e);
      this.showAlertBox({
        title: "Error",
        message: "Something went wrong while downloading result",
      });
    } finally {
      this.loader.closeLoading();
    }
  }

  private addPageDetails() {
    const limit = this.table.pagination.perPage;
    this.historyQuery.limit = limit;
    this.historyQuery.offset = this.table.pagination.pageNo * limit;
  }

  private buildHistoryQuery() {
    const query: any = {
      limit: this.historyQuery.limit,
      offset: this.historyQuery.offset,
      sortBy: this.historyQuery.sortBy,
      sortOrder: this.historyQuery.sortOrder,
    };
    if (this.historyQuery.subTransactionType.length > 0) {
      query.subTransactionType = this.historyQuery.subTransactionType;
    }
    if (this.historyQuery.from) {
      query.from = this.historyQuery.from;
    }
    if (this.historyQuery.to) {
      query.to = this.historyQuery.to;
    }
    return query;
  }

  private fileId(item: any): string {
    if (typeof item._id === "string") {
      return item._id;
    }
    return item._id && item._id.$oid;
  }

  private logUnknownSubTransactionTypes(history: any[]) {
    const configuredTypes = new Set(this.bulkPayService.transactionTypes.map(t => t.name));
    const unknownRecords = history.filter(item => !configuredTypes.has(item.subTransactionType));
    if (unknownRecords.length === 0) {
      return;
    }

    const unknownTypes = Array.from(new Set(unknownRecords.map(item => item.subTransactionType)));
    console.error("[BulkPayHistory] Unknown subTransactionType(s) in history response", {
      unknownTypes,
      fileIds: unknownRecords.map(item => this.fileId(item)),
      configuredTypes: Array.from(configuredTypes),
    });
  }

  private subTransactionTypeName(code: string) {
    const match = this.bulkPayService.transactionTypes.find(t => t.name === code);
    if (!match) {
      console.error("[BulkPayHistory] subTransactionType not found in config", {
        subTransactionType: code,
        configuredTypes: this.bulkPayService.transactionTypes.map(t => t.name),
      });
      return code;
    }
    return match.displayName;
  }

  private subTransactionTypeCode(name: string) {
    const match = this.bulkPayService.transactionTypes.find(t => t.displayName === name);
    if (!match) {
      console.error("[BulkPayHistory] subTransactionType filter not found in config", {
        displayName: name,
        configuredTypes: this.bulkPayService.transactionTypes.map(t => t.name),
      });
      return name;
    }
    return match.name;
  }

  async sortByHeader(header: string) {
    const sortOrder = header.includes("-") ? -1 : 1;
    const column = header.replace("-", "");
    if (!this.sortableColumns.includes(column)) {
      return;
    }

    this.historyQuery.sortBy = column;
    this.historyQuery.sortOrder = sortOrder;

    await this.fetchHistory();
  }

  async applyFilters(newFilters) {
    if (newFilters.subTransactionType && newFilters.subTransactionType.length > 0) {
      this.historyQuery.subTransactionType = newFilters
        .subTransactionType
        .map((name: string) => this.subTransactionTypeCode(name));
    } else {
      this.historyQuery.subTransactionType = [];
    }
    const [from, to] = newFilters.createdAt || [] as string[];
    this.historyQuery.from = from ? moment(from).valueOf() : undefined;
    this.historyQuery.to = to ? moment(to).endOf("day").valueOf() : undefined;

    await this.fetchHistory();
  }
}
